import { DrawingInterface } from "./DrawingInterface";
import { PartialUnknownShape } from "./types/attributes";
import Rect from "./types/Rect";
import { ShapeDOMRepresentation } from "./types/ShapeDOMRepresentation";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";

export class ImageRendererDebugger {
  latestImageURL: string | null = null;

  imageEl: HTMLImageElement;
  renderer: ImageRenderer;

  constructor(private el: HTMLElement, wb: WhiteboardControllerInterface) {
    this.renderer = new ImageRenderer(wb.drawing, 1, false);
    this.imageEl = document.createElement("img");
    this.el.appendChild(this.imageEl);

    this.imageEl.style.position = "absolute";
    this.imageEl.style.top = "30px";
    this.imageEl.style.right = "30px";
    // this.imageEl.style.width = "320px";
    this.imageEl.style.border = "1px solid orange";

    setInterval(() => {
      this.replaceImage();
    }, 1000);
  }

  replaceImage() {
    this.renderer.renderToDataURL().then((url) => (this.imageEl.src = url));
  }
}

export class ImageRenderer {
  constructor(
    private drawing: DrawingInterface,
    private scaleFactor: number,
    private transparentBackground: boolean
  ) {}

  renderToCanvas(
    limit = false,
    immediate = true,
    updateProgress?: (frac: number) => void
  ): Promise<HTMLCanvasElement> {
    console.time("RenderToCanvas");
    const canvas = document.createElement("canvas");

    const boundingRect = this.drawing.findBoundingRect();
    if (limit) {
      const MAX_EXTEND_X = 4096 / 2;
      const MAX_EXTEND_Y = 2304 / 2;
      if (boundingRect.min.x < -MAX_EXTEND_X)
        boundingRect.min.x = -MAX_EXTEND_X;
      if (boundingRect.min.y < -MAX_EXTEND_Y)
        boundingRect.min.y = -MAX_EXTEND_Y;
      if (boundingRect.max.x > MAX_EXTEND_X) boundingRect.max.x = MAX_EXTEND_X;
      if (boundingRect.max.y > MAX_EXTEND_Y) boundingRect.max.y = MAX_EXTEND_Y;
      // const MAX_SIZE = 4096;
      // if (boundingRect.width > MAX_SIZE) {
      //   boundingRect.min.x = boundingRect.centerX - MAX_SIZE / 2;
      //   boundingRect.max.x = boundingRect.min.x + MAX_SIZE;
      // }
      // if (boundingRect.height > MAX_SIZE) {
      //   boundingRect.min.y = boundingRect.centerY - MAX_SIZE / 2;
      //   boundingRect.max.y = boundingRect.min.y + MAX_SIZE;
      // }
    }
    canvas.width = boundingRect.width * this.scaleFactor;
    canvas.height = boundingRect.height * this.scaleFactor;

    const ctx = canvas.getContext("2d");
    if (!ctx) return Promise.resolve(canvas);

    if (!this.transparentBackground) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.translate(
      -boundingRect.min.x * this.scaleFactor,
      -boundingRect.min.y * this.scaleFactor
    );
    ctx.scale(this.scaleFactor, this.scaleFactor);

    const renderOne = (shape: ShapeDOMRepresentation) => {
      if (boundingRect.intersects(shape.boundingRect)) {
        this.drawShape(ctx, shape);
      }
    };

    if (immediate) {
      let i = 0;
      for (const shape of this.drawing.orderedShapes) {
        if (i % 0 === 0 && updateProgress) {
          updateProgress(i / this.drawing.orderedShapes.length);
        }
        renderOne(shape);
        i += 1;
      }
      console.timeEnd("RenderToCanvas");
      return Promise.resolve(canvas);
    } else {
      return new Promise((resolve) => {
        this.remainingShapes = new Array<ShapeDOMRepresentation>().concat(
          this.drawing.orderedShapes
        );
        this.remainingShapesOriginalSize = this.remainingShapes.length;

        this.drawNextBatch(canvas, ctx, boundingRect, updateProgress, resolve);
      });
    }
  }

  private drawnShapes = new Set<string>();
  private remainingShapes = new Array<ShapeDOMRepresentation>();
  private remainingShapesOriginalSize = 0;
  private drawNextBatch(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    boundingRect: Rect,
    updateProgress: undefined | ((frac: number) => void),
    resolve: (canvas: HTMLCanvasElement) => void
  ) {
    const nextBatch = this.remainingShapes.slice(0, 50);
    for (const shape of nextBatch) {
      if (boundingRect.intersects(shape.boundingRect)) {
        this.drawShape(ctx, shape);
      }
    }

    this.remainingShapes = this.remainingShapes.slice(
      50,
      this.remainingShapes.length
    );
    if (updateProgress) {
      updateProgress(
        1 - this.remainingShapes.length / this.remainingShapesOriginalSize
      );
    }

    if (this.remainingShapes.length) {
      setTimeout(
        () =>
          this.drawNextBatch(
            canvas,
            ctx,
            boundingRect,
            updateProgress,
            resolve
          ),
        0
      );
    } else {
      resolve(canvas);
    }
  }

  renderToDataURL(updateProgress?: (frac: number) => void): Promise<string> {
    return this.renderToCanvas(false, false, updateProgress).then((canvas) =>
      canvas.toDataURL()
    );
  }

  drawShape(ctx: CanvasRenderingContext2D, shape: ShapeDOMRepresentation) {
    if (this.drawnShapes.has(shape.id)) {
      console.warn("Duplicate shape:", shape.id);
      return;
    }
    this.drawnShapes.add(shape.id);
    ctx.save();
    const localScaleFactor = Math.max(1, this.scaleFactor);
    const shapeCanvas = shape.renderShapeToCanvas(localScaleFactor);
    shape.applyCanvasTransformToCanvas(ctx);

    const shapeWithAlpha = shape.shape as PartialUnknownShape;
    if (shapeWithAlpha.alpha) {
      ctx.globalAlpha = shapeWithAlpha.alpha;
    } else {
      ctx.globalAlpha = 1;
    }
    ctx.scale(1 / localScaleFactor, 1 / localScaleFactor);
    try {
      ctx.drawImage(shapeCanvas, 0, 0);
    } catch (e) {
      // Can't really do anything
    }
    ctx.restore();
  }
}
